import React, { useCallback, useMemo, useState } from 'react';
import { RadioButton } from 'react-native-paper';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { Formik } from 'formik';
import * as yup from 'yup';

import { FormikHelpers } from 'formik/dist/types';
import Container from '../../components/Container';
import Typography from '../../components/Typography';
import FormikInput from '../../components/formik/FormikInput';
import { useModal } from '../../context/ModalContext';
import * as adminApi from '../../apis/adminApis';

import Radio from '../../components/ui/Radio';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { AdminConfirmModalContent, RawAdminCommandOutput, SendAdminCommandButton } from './AdminTools';

interface RequestForm {
  batchId: string;
  enabled: 'true' | 'false';
}

export default function EnablePromoCode({ navigation }: { navigation: DrawerNavigationProp<any> }) {
  const [initForm] = useState<RequestForm>({
    batchId: '',
    enabled: 'false',
  });
  const [request, setRequest] = useState<Promise<any>>();

  const modal = useModal();

  const validateSchema = useMemo(() => {
    return yup.object().shape({
      batchId: yup.string().required(),
      enabled: yup.boolean().required(),
    });
  }, []);

  const handleAction = useCallback(
    (form: RequestForm) => {
      const p = adminApi.vouchersEnableByBatchId(form.batchId, form.enabled === 'true');
      p.finally(() => modal.closeModal());
      setRequest(p);
      return p;
    },
    [modal],
  );

  const handleSubmitForm = useCallback(
    async (form: RequestForm, formikHelper: FormikHelpers<RequestForm>) => {
      modal.openModal({
        content: <AdminConfirmModalContent onDismiss={() => modal.closeModal()} onConfirm={() => handleAction(form)} />,
      });
    },
    [modal, handleAction],
  );

  return (
    <ScrollablePage
      summary={{
        title: 'Enable Voucher batch',
        description: 'Enable a batch of vouchers',
        icon: 'pencil',
      }}
    >
      <Container>
        <Formik initialValues={initForm} onSubmit={handleSubmitForm} validationSchema={validateSchema}>
          {({ values, handleChange, handleSubmit, isSubmitting }) => (
            <>
              <FormikInput
                name={'batchId'}
                mode="outlined"
                keyboardType="default"
                autoComplete="off"
                autoCapitalize="sentences"
                label={'Batch id (aka internalNote)'}
              />
              <Typography variant="body2">Status</Typography>
              <RadioButton.Group onValueChange={handleChange('enabled')} value={values.enabled}>
                <Radio label={'Enabled'} value="true" />
                <Radio label={'Disabled'} value="false" />
              </RadioButton.Group>

              <SendAdminCommandButton handleSubmit={handleSubmit} isSubmitting={isSubmitting} />
            </>
          )}
        </Formik>
        <RawAdminCommandOutput request={request} />
      </Container>
    </ScrollablePage>
  );
}
